import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { cn } from "@/projects/lib/utils";
import {
  getFailedPasswordRules,
  getPasswordRequirementMessages,
  isPasswordValid,
  PASSWORD_MAX_LENGTH,
  type PasswordPersonalContext,
} from "@/projects/lib/passwordValidation";

const MAX_VISIBLE_ERRORS = 3;

type PasswordFieldsProps = {
  password: string;
  confirmPassword: string;
  onPasswordChange: (value: string) => void;
  onConfirmChange: (value: string) => void;
  passwordLabel?: string;
  confirmLabel?: string;
  passwordId?: string;
  confirmId?: string;
  disabled?: boolean;
  className?: string;
  /** When true, fields span full width (stacked). Default true. */
  stacked?: boolean;
  personalContext?: PasswordPersonalContext;
  /** Force showing validation after submit attempt */
  showErrors?: boolean;
};

export function PasswordFields({
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmChange,
  passwordLabel = "New Password",
  confirmLabel = "Confirm Password",
  passwordId = "new_password",
  confirmId = "confirm_password",
  disabled = false,
  className,
  stacked = true,
  personalContext,
  showErrors = false,
}: PasswordFieldsProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  const failedRules = getFailedPasswordRules(password, personalContext);
  const passwordInvalid = password.length > 0 && failedRules.length > 0;
  const passwordValid = password.length > 0 && failedRules.length === 0;
  const showSuccess = passwordValid && (passwordTouched || showErrors);
  const showPasswordErrors =
    passwordInvalid && (passwordTouched || showErrors) && !showRequirements;
  const visibleErrors = failedRules.slice(0, MAX_VISIBLE_ERRORS);
  const hiddenErrorCount = Math.max(0, failedRules.length - MAX_VISIBLE_ERRORS);

  const confirmMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;
  const showConfirmError =
    confirmMismatch && (confirmTouched || showErrors || passwordTouched);
  const showConfirmSuccess =
    confirmPassword.length > 0 &&
    !confirmMismatch &&
    passwordValid &&
    (confirmTouched || showErrors);

  const requirements = getPasswordRequirementMessages();

  return (
    <div className={cn(stacked ? "space-y-4" : "contents", className)}>
      <div className={cn(!stacked && "sm:col-span-2")}>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <Label htmlFor={passwordId}>{passwordLabel}</Label>
          <button
            type="button"
            onClick={() => setShowRequirements(v => !v)}
            className="text-xs font-medium text-[#2563eb] hover:underline"
          >
            {showRequirements ? "Hide Requirements" : "Show Requirements"}
          </button>
        </div>
        <div className="relative">
          <Input
            id={passwordId}
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={password}
            maxLength={PASSWORD_MAX_LENGTH}
            onChange={e => {
              onPasswordChange(e.target.value);
              if (!passwordTouched) {
                setPasswordTouched(true);
              }
            }}
            onBlur={() => setPasswordTouched(true)}
            disabled={disabled}
            placeholder="Enter new password (8-10 characters)"
            className={cn(
              "pr-10",
              showPasswordErrors && "border-red-400 focus-visible:ring-red-400",
              showSuccess && "border-emerald-500 focus-visible:ring-emerald-500"
            )}
            aria-invalid={showPasswordErrors}
          />
          <button
            type="button"
            onClick={() => setShowPassword(s => !s)}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 rounded p-1.5"
            aria-label={showPassword ? "Hide password" : "Show password"}
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        {showRequirements && !passwordValid && (
          <div className="mt-2 max-h-36 overflow-y-auto rounded-md border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2 text-xs text-[#1e40af]">
            <div className="mb-1 font-semibold">Password Requirements:</div>
            <ul className="list-disc space-y-0.5 pl-4">
              {requirements.map(req => (
                <li key={req}>{req}</li>
              ))}
            </ul>
          </div>
        )}

        {showSuccess && (
          <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
            ✓ Password meets all requirements
          </div>
        )}

        {showPasswordErrors && (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
            <ul className="list-disc space-y-0.5 pl-4">
              {visibleErrors.map(rule => (
                <li key={rule.id}>{rule.error}</li>
              ))}
              {hiddenErrorCount > 0 && <li>...and {hiddenErrorCount} more</li>}
            </ul>
          </div>
        )}
      </div>

      <div className={cn("mt-3", !stacked && "sm:col-span-2 mt-4")}>
        <Label htmlFor={confirmId} className="mb-1.5 block">
          {confirmLabel}
        </Label>
        <div className="relative">
          <Input
            id={confirmId}
            type={showConfirm ? "text" : "password"}
            autoComplete="new-password"
            value={confirmPassword}
            maxLength={PASSWORD_MAX_LENGTH}
            onChange={e => {
              onConfirmChange(e.target.value);
              if (!confirmTouched) {
                setConfirmTouched(true);
              }
            }}
            onBlur={() => setConfirmTouched(true)}
            disabled={disabled}
            placeholder="Confirm new password"
            className={cn(
              "pr-10",
              showConfirmError && "border-red-400 focus-visible:ring-red-400",
              showConfirmSuccess &&
                "border-emerald-500 focus-visible:ring-emerald-500"
            )}
            aria-invalid={showConfirmError}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(s => !s)}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 rounded p-1.5"
            aria-label={showConfirm ? "Hide password" : "Show password"}
            tabIndex={-1}
          >
            {showConfirm ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {showConfirmError && (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
            Passwords do not match
          </div>
        )}
      </div>
    </div>
  );
}

export function canSubmitPasswords(
  password: string,
  confirmPassword: string,
  personalContext?: PasswordPersonalContext
): { ok: true } | { ok: false; message: string } {
  if (!password || !confirmPassword) {
    return { ok: false, message: "Please enter and confirm your password." };
  }
  if (!isPasswordValid(password, personalContext)) {
    const failed = getFailedPasswordRules(password, personalContext);
    return {
      ok: false,
      message: failed[0]?.error ?? "Password does not meet the requirements.",
    };
  }
  if (password !== confirmPassword) {
    return { ok: false, message: "Passwords do not match" };
  }
  return { ok: true };
}
